import type {QueryClient} from "@tanstack/react-query";
import type {ZodType} from "zod";

import type {Result} from "@vesta/error-handling-remix/result";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {countryValidator, type Country} from "@vesta/vos-types/modules/platformLevel/geography/domain/Country";
import {indiaCityValidator, type IndiaCity} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaCity";
import {indiaPincodeValidator, type IndiaPincode} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaPincode";
import {indiaStateValidator, type IndiaState} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaState";
import {indiaZoneValidator, type IndiaZone} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaZone";
import type {GeographyCollections} from "~/contexts/geographyContext";
import {buildPlutoCacheableGetUrl} from "~/utilities/plutoCacheableGetUrl";
import {initializeGeographyValueResolver} from "~/utilities/geographyValueResolver";
import {
    COUNTRIES__QUERY_KEY,
    INDIA_CITIES__QUERY_KEY,
    INDIA_PINCODES__QUERY_KEY,
    INDIA_STATES__QUERY_KEY,
    INDIA_ZONES__QUERY_KEY,
} from "~/utilities/queryKeys";

async function getGeographyMasterWithCaching<TItem>({
    intellsysBaseUrl,
    path,
    queryClient,
    queryKey,
    validator,
}: {
    intellsysBaseUrl: string;
    path: string;
    queryClient: QueryClient;
    queryKey: string;
    validator: ZodType<TItem>;
}): Promise<Result<Array<TItem>>> {
    return queryClient.ensureQueryData({
        queryKey: [queryKey],
        queryFn: () =>
            robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({input: {}, intellsysBaseUrl: intellsysBaseUrl, path: path}),
                {method: "GET"},
                validator.array(),
            ),
        retry: false,
        staleTime: Infinity,
    });
}

function unwrap<TItem>(result: Result<Array<TItem>>): Array<TItem> {
    if (result.success !== true) {
        throw result.error;
    }
    return result.data;
}

export async function getGeographyWithCaching({
    intellsysBaseUrl,
    queryClient,
}: {
    intellsysBaseUrl: string;
    queryClient: QueryClient;
}): Promise<GeographyCollections> {
    const [countriesResult, indiaCitiesResult, indiaPincodesResult, indiaStatesResult, indiaZonesResult] = await Promise.all([
        getGeographyMasterWithCaching<Country>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/countries/get",
            queryClient: queryClient,
            queryKey: COUNTRIES__QUERY_KEY,
            validator: countryValidator,
        }),
        getGeographyMasterWithCaching<IndiaCity>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-cities/get",
            queryClient: queryClient,
            queryKey: INDIA_CITIES__QUERY_KEY,
            validator: indiaCityValidator,
        }),
        // TODO: Fetch pincodes in batches instead of loading the complete master during profile bootstrap.
        getGeographyMasterWithCaching<IndiaPincode>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-pincodes/get",
            queryClient: queryClient,
            queryKey: INDIA_PINCODES__QUERY_KEY,
            validator: indiaPincodeValidator,
        }),
        getGeographyMasterWithCaching<IndiaState>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-states/get",
            queryClient: queryClient,
            queryKey: INDIA_STATES__QUERY_KEY,
            validator: indiaStateValidator,
        }),
        getGeographyMasterWithCaching<IndiaZone>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-zones/get",
            queryClient: queryClient,
            queryKey: INDIA_ZONES__QUERY_KEY,
            validator: indiaZoneValidator,
        }),
    ]);

    const geography = {
        countries: unwrap(countriesResult),
        indiaCities: unwrap(indiaCitiesResult),
        indiaPincodes: unwrap(indiaPincodesResult),
        indiaStates: unwrap(indiaStatesResult),
        indiaZones: unwrap(indiaZonesResult),
    };
    initializeGeographyValueResolver(geography);
    return geography;
}
